import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Check, KeyRound, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const INGEST_URL = `${BACKEND_URL}/api/cards`;

export default function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyReveal, setNewKeyReveal] = useState(null); // {full_key, key_prefix, label}
  const [docsOpen, setDocsOpen] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminApi.listApiKeys();
      setKeys(d.items || []);
    } catch { toast.error("Failed to load API keys"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (label) => {
    try {
      const d = await adminApi.createApiKey(label);
      setNewKeyReveal(d);
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create key");
    }
  };

  const onRevoke = async (key_id, label) => {
    if (!window.confirm(`Revoke API key "${label}"? Any script or n8n workflow using it will stop working immediately.`)) return;
    try {
      await adminApi.revokeApiKey(key_id);
      toast.success("Key revoked");
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[#F8FAFC]">Programmatic API keys</h2>
          <p className="text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Push cards from n8n, scripts, or webhooks. Keep secret — anyone with the key can create cards.</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="apikeys-create-button"
          className="inline-flex items-center gap-2 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2 text-sm font-semibold hover:bg-[#C94A1D] transition-colors"
        >
          <KeyRound className="h-4 w-4" aria-hidden />
          Generate new key
        </button>
      </div>

      {/* Documentation card (collapsible) */}
      <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden">
        <button
          type="button"
          onClick={() => setDocsOpen(!docsOpen)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#232E45] transition-colors text-left"
        >
          <div>
            <div className="text-sm font-semibold text-[#F8FAFC]">How to use these keys (n8n / curl / Python)</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">Endpoint URL, headers, and sample payloads</div>
          </div>
          {docsOpen ? <ChevronUp className="h-4 w-4 text-[#94A3B8]" /> : <ChevronDown className="h-4 w-4 text-[#94A3B8]" />}
        </button>
        {docsOpen && (
          <div className="border-t border-[#26334D] p-5 sm:p-6 space-y-4">
            <FieldRow label="Endpoint URL" value={INGEST_URL} />
            <FieldRow label="Method" value="POST" mono />
            <FieldRow label="Header 1" value="X-API-Key: dst_live_..." mono />
            <FieldRow label="Header 2" value="Content-Type: application/json" mono />
            <FieldRow label="Rate limit" value="60 requests / minute per key" />

            <div>
              <div className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Sample JSON body (only headline + summary_60 required)</div>
              <CodeBlock>{SAMPLE_JSON}</CodeBlock>
            </div>

            <div>
              <div className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">n8n HTTP Request node config</div>
              <ul className="mt-2 text-xs text-[#F8FAFC] space-y-1" style={{ lineHeight: 1.7 }}>
                <li>&bull; <strong>Method:</strong> POST</li>
                <li>&bull; <strong>URL:</strong> <code className="rounded bg-[#111622] px-1 py-0.5">{INGEST_URL}</code></li>
                <li>&bull; <strong>Authentication:</strong> Generic Credential Type &rarr; <em>Header Auth</em></li>
                <li>&bull; <strong>Header Name:</strong> <code className="rounded bg-[#111622] px-1 py-0.5">X-API-Key</code></li>
                <li>&bull; <strong>Header Value:</strong> paste your <code className="rounded bg-[#111622] px-1 py-0.5">dst_live_...</code> key</li>
                <li>&bull; <strong>Send Body:</strong> ON &rarr; <em>JSON</em> &rarr; paste the sample above (map fields from your previous nodes)</li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Sample curl</div>
              <CodeBlock>{`curl -X POST ${INGEST_URL} \\
  -H "X-API-Key: dst_live_YOUR_KEY_HERE" \\
  -H "Content-Type: application/json" \\
  -d @card.json`}</CodeBlock>
            </div>

            <div className="text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
              <strong>Response:</strong> <code>200</code> with <code>{`{ ok: true, card: { card_id, ... } }`}</code>.
              &nbsp; <strong>Errors:</strong> <code>401</code> = invalid key, <code>400</code> = bad JSON or summary word-count outside 40&ndash;90, <code>429</code> = rate limited.
            </div>
          </div>
        )}
      </div>

      {/* Keys table */}
      {loading ? (
        <div className="h-32 rounded-2xl bg-[#1A2232] border border-[#26334D] animate-pulse" />
      ) : keys.length === 0 ? (
        <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-6 text-center">
          <p className="text-sm font-semibold text-[#F8FAFC]">No API keys yet.</p>
          <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Generate your first key to enable n8n / script ingestion.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#111622] text-[#94A3B8]">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Label</th>
                <th className="text-left px-4 py-2 font-semibold">Key prefix</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Used</th>
                <th className="text-left px-4 py-2 font-semibold">Created</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id} className="border-t border-[#26334D]" data-testid="apikey-row">
                  <td className="px-4 py-3 text-[#F8FAFC]">{k.label}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{k.key_prefix}…</td>
                  <td className="px-4 py-3">
                    {k.is_revoked ? (
                      <span className="rounded-full bg-[#B42318]/10 text-[#B42318] px-2 py-0.5 text-[11px] font-semibold">revoked</span>
                    ) : (
                      <span className="rounded-full bg-[#2F6F4F]/10 text-[#2F6F4F] px-2 py-0.5 text-[11px] font-semibold">active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8]">
                    {k.usage_count || 0}
                    {k.last_used_at && (
                      <span className="ml-2 text-xs">· last {formatDistanceToNow(new Date(k.last_used_at), { addSuffix: true })}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8]">{formatDistanceToNow(new Date(k.created_at), { addSuffix: true })}</td>
                  <td className="px-4 py-3 text-right">
                    {!k.is_revoked && (
                      <button
                        onClick={() => onRevoke(k.key_id, k.label)}
                        data-testid="apikey-revoke"
                        className="inline-flex items-center gap-1 text-[#B42318] font-semibold hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={onCreate} />
      <RevealKeyDialog data={newKeyReveal} onClose={() => setNewKeyReveal(null)} />
    </div>
  );
}

const SAMPLE_JSON = `{
  "headline": "16th Finance Commission Constituted",
  "summary_60": "Union government constituted the 16th Finance Commission under Arvind Panagariya to recommend tax devolution between Centre and States for 2026-31. Terms of reference include vertical sharing of the divisible pool, horizontal criteria across states, grants-in-aid, and disaster-management financing. Commission must submit its report by October 2025 for implementation from April 2026 onward, replacing 15th Commission recommendations that expire.",
  "category": "GS-3",
  "pib_tag": true,
  "data_tag": false,
  "ethics_tag": false,
  "pyq_tag": "Prelims 2019",
  "prelims_pointers": [
    "Article 280 establishes Finance Commission every 5 years.",
    "Chairman: Arvind Panagariya."
  ],
  "mains_utility": "GS-2 Federalism: fiscal federalism, vertical/horizontal imbalance.",
  "source": "PIB",
  "source_url": "https://pib.gov.in",
  "image_url": "https://example.com/image.jpg",
  "tags": ["#GS3", "#Economy", "#Federalism"]
}`;

function FieldRow({ label, value, mono = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[apikeys] Clipboard copy failed:", err?.message || err);
      toast.error("Copy failed — select and copy manually.");
    }
  };
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase w-32 shrink-0">{label}</div>
      <div className={`flex-1 rounded-lg bg-[#111622] border border-[#26334D] px-3 py-2 text-xs ${mono ? "font-mono" : ""} text-[#F8FAFC] break-all`}>{value}</div>
      <button type="button" onClick={copy} className="shrink-0 h-8 w-8 rounded-lg border border-[#26334D] bg-[#1A2232] text-[#94A3B8] hover:bg-[#232E45] inline-flex items-center justify-center" aria-label={`Copy ${label}`}>
        {copied ? <Check className="h-3.5 w-3.5 text-[#2F6F4F]" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function CodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[apikeys] Clipboard copy failed:", err?.message || err);
      toast.error("Copy failed — select and copy manually.");
    }
  };
  return (
    <div className="mt-2 relative rounded-xl bg-[#0B1220] text-[#26334D] p-4 overflow-x-auto">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 h-8 w-8 rounded-lg bg-[#F8FAFC]/10 hover:bg-[#F8FAFC]/20 text-white inline-flex items-center justify-center"
        aria-label="Copy code"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-[#7DE3B6]" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      <pre className="text-xs font-mono whitespace-pre-wrap pr-10" style={{ lineHeight: 1.55 }}>{children}</pre>
    </div>
  );
}

function CreateKeyDialog({ open, onClose, onCreate }) {
  const [label, setLabel] = useState("n8n workflow");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    await onCreate(label.trim());
    setBusy(false);
    setLabel("n8n workflow");
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
          <motion.form initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-[440px] rounded-2xl bg-[#1A2232] border border-[#26334D] p-6 shadow-[0_20px_60px_rgba(15,23,42,0.20)]">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#F8FAFC]">Generate a new API key</h3>
              <button type="button" onClick={onClose} className="h-8 w-8 rounded-lg text-[#94A3B8] hover:bg-[#232E45] inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Give it a memorable name so you know which script uses it.</p>
            <label className="mt-4 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Label</label>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              placeholder="e.g. n8n morning batch"
              data-testid="apikey-label-input"
              className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            />
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45]">Cancel</button>
              <button type="submit" disabled={busy} data-testid="apikey-create-submit" className="flex-1 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2.5 text-sm font-semibold hover:bg-[#C94A1D] disabled:opacity-50">{busy ? "Generating…" : "Generate key"}</button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function RevealKeyDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!data?.full_key_shown_once) return;
    try {
      await navigator.clipboard.writeText(data.full_key_shown_once);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — select the text manually."); }
  };
  return (
    <AnimatePresence>
      {data && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[85] bg-black/40 flex items-center justify-center p-4">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="w-full max-w-[560px] rounded-2xl bg-[#1A2232] border border-[#26334D] p-6 shadow-[0_20px_60px_rgba(15,23,42,0.25)]" role="dialog">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl bg-[#E85A2A] text-[#F8FAFC] inline-flex items-center justify-center"><KeyRound className="h-4 w-4" /></span>
              <div>
                <h3 className="text-base font-semibold text-[#F8FAFC]">Your new API key</h3>
                <p className="text-xs text-[#94A3B8]">Label: <strong>{data.label}</strong></p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-[#F2BD68]/40 bg-[#F2BD68]/5 p-3">
              <p className="text-xs text-[#F2BD68]" style={{ lineHeight: 1.6 }}>
                <strong>Copy this now.</strong> For your security, the full key will <strong>never be shown again</strong>. If you lose it, revoke and generate a new one.
              </p>
            </div>

            <div className="mt-4 rounded-xl bg-[#0B1220] text-[#26334D] p-4 font-mono text-xs break-all" data-testid="apikey-reveal-value">
              {data.full_key_shown_once}
            </div>

            <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-1/3 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45]"
              >I saved it — close</button>
              <button
                type="button"
                onClick={copy}
                data-testid="apikey-reveal-copy"
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2.5 text-sm font-semibold hover:bg-[#C94A1D]"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied to clipboard" : "Copy full key"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
